import { DndContext } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useCallback, useEffect, useRef } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import ComponentPanel from './ComponentPanel/ComponentPanel';
import { useDragAndDrop } from './hooks/useDragAndDrop';
import { usePropertyBuilder } from './hooks/usePropertyBuilder';
import Modal from './Modal/Modal';
import PlaceholderLoader from './PlaceholderLoader/PlaceholderLoader';
import Tab from './Tab/Tab';

const AddPropertyFormTab = ({
  showToast,
  onActionsReady,
  isSaving,
  isResetting
}) => {
  const {
    tabs,
    setTabs,
    activeTab,
    activeField,
    isTabModalOpen,
    setIsTabModalOpen,
    isFieldModalOpen,
    setIsFieldModalOpen,
    isLoading,
    groupCollapsedStates,
    openTabModal,
    openFieldModal,
    saveConfiguration,
    resetConfiguration,
    addTab,
    editTab,
    deleteTab,
    toggleTabCollapse,
    addField,
    editField,
    deleteField,
    toggleField,
    toggleFieldRequired,
    toggleFieldAdminOnly,
    moveFieldUp,
    moveFieldDown,
    moveGroupUp,
    moveGroupDown,
    toggleGroupCollapse,
    deleteGroup,
    toggleSidebar,
    isRequiredTab
  } = usePropertyBuilder(showToast);

  const {
    sensors,
    dragOverTabId,
    dragDirection,
    handleDragStart,
    handleDragMove,
    handleDragOver,
    handleDragEnd
  } = useDragAndDrop({ tabs, setTabs, showToast });

  const safeTabs = Array.isArray(tabs) ? tabs : Object.values(tabs || []);
  const hasSentActions = useRef(false);
  const hasShownLoadToast = useRef(false); // Track if load toast has been shown

  const tabsRef = useRef(tabs);
  const saveConfigRef = useRef(saveConfiguration);
  const resetConfigRef = useRef(resetConfiguration);
  
  useEffect(() => {
    tabsRef.current = tabs;
    saveConfigRef.current = saveConfiguration;
    resetConfigRef.current = resetConfiguration;
  }, [tabs, saveConfiguration, resetConfiguration]);

  // Show "Configuration Loaded" toast when loading completes
  useEffect(() => {
    if (!isLoading && !hasShownLoadToast.current && tabs.length > 0) {
      hasShownLoadToast.current = true;
      showToast('success', __('Configuration Loaded', 'havenlytics'), __('Form sections loaded successfully', 'havenlytics'));
    }
  }, [isLoading, tabs.length, showToast]);

  const stableSaveConfiguration = useCallback(async () => {
    if (!tabsRef.current || tabsRef.current.length === 0) {
      showToast('error', __('Save Failed', 'havenlytics'), __('No data to save. Please add some sections and fields first.', 'havenlytics'));
      return false;
    }
    
    const result = await saveConfigRef.current();
    return result;
  }, [showToast]);

  const stableResetConfiguration = useCallback(async () => {
    const result = await resetConfigRef.current();
    return result;
  }, []);

  // Expose actions to parent component
  useEffect(() => {
    if (onActionsReady && !hasSentActions.current) {
      hasSentActions.current = true;
      
      const actions = {
        saveConfiguration: stableSaveConfiguration,
        resetConfiguration: stableResetConfiguration,
        isLoading
      };
      
      onActionsReady(actions);
    }
  }, [onActionsReady, stableSaveConfiguration, stableResetConfiguration, isLoading]);

  const getExistingFields = () => {
    if (!activeTab) return [];
    const tab = tabs.find(t => t.id === activeTab);
    return tab ? tab.fields : [];
  };

  const getActiveTabObject = () => {
    if (!activeTab) return null;
    return tabs.find(t => t.id === activeTab);
  };

  const handleSaveField = (fieldData) => {
    if (!activeTab) {
      showToast('error', __('Error', 'havenlytics'), __('No active tab selected', 'havenlytics'));
      return;
    }

    if (Array.isArray(fieldData)) {
      let successCount = 0;
      
      fieldData.forEach((field, index) => {
        try {
          addField(activeTab, field);
          successCount++;
        } catch (error) {
         // console.error('Error adding field:', error);
        }
      });
      
      if (successCount > 0) {
        if (fieldData.length > 1) {
          showToast('success', __('Fields Added', 'havenlytics'), `${successCount} of ${fieldData.length} ` + __('fields added successfully', 'havenlytics'));
        } else {
          showToast('success', __('Field Added', 'havenlytics'), __('Field added successfully', 'havenlytics'));
        }
      } else {
        showToast('error', __('Add Failed', 'havenlytics'), __('Failed to add fields', 'havenlytics'));
      }
      
      setIsFieldModalOpen(false);
    } else {
      try {
        if (activeField) {
          editField(activeTab, activeField.id, fieldData);
          showToast('success', __('Field Updated', 'havenlytics'), __('Field updated successfully', 'havenlytics'));
        } else {
          addField(activeTab, fieldData);
          showToast('success', __('Field Added', 'havenlytics'), __('Field added successfully', 'havenlytics'));
        }
        setIsFieldModalOpen(false);
      } catch (error) {
       // console.error('Error processing field:', error);
        showToast('error', __('Operation Failed', 'havenlytics'), __('Failed to process field', 'havenlytics'));
      }
    }
  };

  const checkRequiredTab = (tab) => {
    return isRequiredTab ? isRequiredTab(tab) : (tab.required || tab.title === 'Basic Info');
  };

  return (
    <div className="hvnly--builder--int_api__tab_content_panel">
      <div className="hvnly--builder--int_api__property_builder_content">
        <ComponentPanel />
        
        <div className="hvnly--builder--int_api__property_builder_area">
          <div className="hvnly--builder--int_api__property_builder_section_header">
            <h3 className="hvnly--builder--int_api__property_builder_section_header_title">
              {__('Property Section & Fields', 'havenlytics')}
            </h3>
            
            <div className="hvnly--builder--int_api__property_builder_stats">
              <span>{__('Total:', 'havenlytics')}</span>
              <span className="hvnly--builder--int_api__property_builder_stats_sections">
                {safeTabs.length} {__('sections', 'havenlytics')}
              </span>
              <span className="hvnly--builder--int_api__property_builder_stats_fields">
                {safeTabs.reduce((acc, tab) => acc + (tab.fields?.length || 0), 0)} {__('fields', 'havenlytics')}
              </span>
            </div>
          </div>
          
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragMove={handleDragMove}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            <SortableContext 
              items={safeTabs.map(tab => tab.id)} 
              strategy={verticalListSortingStrategy}
            >
              <div className="hvnly--builder--int_api__property_builder_tabs">
                {isLoading ? (
                  <PlaceholderLoader count={2} />
                ) : safeTabs.length > 0 ? (
                  safeTabs.map((tab, index) => (
                    <Tab
                      key={`${tab.id}-${tab.fields?.length || 0}-${index}`}
                      id={tab.id}
                      tab={{ ...tab, fields: Array.isArray(tab.fields) ? tab.fields : [] }}
                      isDragOver={dragOverTabId === tab.id}
                      dragDirection={dragDirection}
                      onEdit={() => openTabModal(tab)}
                      onDelete={() => deleteTab(tab.id)}
                      onToggleCollapse={() => toggleTabCollapse(tab.id)}
                      onAddField={() => openFieldModal(tab.id)}
                      onEditField={(fieldId) => {
                        const field = (tab.fields || []).find(f => f.id === fieldId);
                        openFieldModal(tab.id, field);
                      }}
                      onDeleteField={(fieldId) => deleteField(tab.id, fieldId)}
                      onDeleteGroup={(groupId) => deleteGroup(groupId)}
                      onToggleField={(fieldId) => toggleField(tab.id, fieldId)}
                      onToggleFieldRequired={(fieldId) => toggleFieldRequired(tab.id, fieldId)}
                      onToggleFieldAdminOnly={(fieldId) => toggleFieldAdminOnly(tab.id, fieldId)}
                      onToggleGroupCollapse={(groupId) => toggleGroupCollapse(groupId)}
                      onToggleSidebar={toggleSidebar}
                      onMoveFieldUp={(fieldId) => moveFieldUp(tab.id, fieldId)}
                      onMoveFieldDown={(fieldId) => moveFieldDown(tab.id, fieldId)}
                      onMoveGroupUp={(groupId, groupIndex) => moveGroupUp(groupId, groupIndex)}
                      onMoveGroupDown={(groupId, groupIndex) => moveGroupDown(groupId, groupIndex)}
                      showToast={showToast}
                      isRequiredTab={checkRequiredTab}
                      groupCollapsedStates={groupCollapsedStates}
                    />
                  ))
                ) : (
                  <div className="hvnly--builder--int_api__property_builder_empty_state">
                    <i className="fas fa-folder-open"></i>
                    <p>{__('No sections found. Add your first section to get started.', 'havenlytics')}</p>
                  </div>
                )}
              </div>
            </SortableContext>
            
            {!isLoading && safeTabs.length > 0 && (
              <button 
                className="hvnly--builder--int_api__property_builder_empty_btn" 
                onClick={() => openTabModal()}
              >
                <i className="fas fa-plus"></i> {__('Add New Section', 'havenlytics')}
              </button>
            )}
          </DndContext>
        </div>
      </div>
      
      {/* Modals */}
      {isTabModalOpen && (
        <Modal
          key={`tab-${activeTab || 'new'}`}
          type="tab"
          data={getActiveTabObject()}
          onSave={activeTab ? (data) => editTab(activeTab, data) : addTab}
          onClose={() => setIsTabModalOpen(false)}
        />
      )}
      
      {isFieldModalOpen && (
        <Modal
          key={`field-${activeField?.id || 'new'}`}
          type="field"
          data={activeField}
          existingFields={getExistingFields()}
          onSave={handleSaveField}
          onClose={() => {
            setIsFieldModalOpen(false);
          }}
          allowMultiple={!activeField}
        />
      )}
      
      {(isSaving || isResetting) && (
        <div className="hvnly--builder--int_api__saving_indicator">
          <i className="fas fa-spinner fa-spin"></i> 
          {isSaving ? __('Saving changes...', 'havenlytics') : __('Resetting configuration...', 'havenlytics')}
        </div>
      )}
    </div>
  );
};

export default AddPropertyFormTab;